import { useState, useRef, useCallback, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { TokenInfo, RefreshEvent, TOKEN_REFRESH_BUFFER, TOKEN_CHECK_INTERVAL, log } from './types';

const LOG_CTX = 'TokenRefresh';

export const useTokenRefresh = () => {
  const { isAuthenticated, isLoading, getAccessTokenSilently, getIdTokenClaims } = useAuth0();

  const [tokenInfo, setTokenInfo] = useState<TokenInfo>({ accessExp: null, idExp: null });
  const [refreshCount, setRefreshCount] = useState(0);
  const [lastRefresh, setLastRefresh] = useState<RefreshEvent | null>(null);
  const [refreshLog, setRefreshLog] = useState<RefreshEvent[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const prevAccessExpRef = useRef<number | null>(null);
  const refreshInProgressRef = useRef(false);
  const refreshFailedRef = useRef(false);
  const tokenInfoRef = useRef<TokenInfo>(tokenInfo);
  tokenInfoRef.current = tokenInfo;

  const fetchTokenInfo = useCallback(
    async (force = false): Promise<TokenInfo | null> => {
      if (!isAuthenticated || refreshInProgressRef.current) return null;

      refreshInProgressRef.current = true;
      setIsRefreshing(true);
      setError(null);

      try {
        const token = await getAccessTokenSilently(force ? { cacheMode: 'off' } : undefined);
        const payload = JSON.parse(atob(token.split('.')[1]));
        const newAccessExp = payload.exp * 1000;

        const oldExp = prevAccessExpRef.current;
        if (oldExp && newAccessExp > oldExp) {
          const event: RefreshEvent = {
            time: Date.now(),
            oldExp,
            newExp: newAccessExp,
          };
          setRefreshCount((c) => c + 1);
          setLastRefresh(event);
          setRefreshLog((logs) => [event, ...logs].slice(0, 20));
          log(LOG_CTX, `🔃 Token refreshed`, {
            oldExp: new Date(oldExp).toLocaleTimeString(),
            newExp: new Date(newAccessExp).toLocaleTimeString(),
            extendedBy: `${Math.round((newAccessExp - oldExp) / 1000)}s`,
          });
        }

        prevAccessExpRef.current = newAccessExp;

        const idClaims = await getIdTokenClaims();
        const newIdExp = idClaims?.exp ? idClaims.exp * 1000 : null;

        const newTokenInfo = { accessExp: newAccessExp, idExp: newIdExp };
        setTokenInfo(newTokenInfo);

        return newTokenInfo;
      } catch (e) {
        const msg = e instanceof Error ? e.message : 'Unknown error';
        setError(msg);
        refreshFailedRef.current = true;
        log(LOG_CTX, `❌ Token refresh failed permanently: ${msg}`);
        return null;
      } finally {
        refreshInProgressRef.current = false;
        setIsRefreshing(false);
      }
    },
    [isAuthenticated, getAccessTokenSilently, getIdTokenClaims],
  );

  useEffect(() => {
    if (!isAuthenticated || isLoading) return;

    const checkAndRefresh = async () => {
      // Stop checking if refresh has failed - user will be logged out when token expires
      if (refreshFailedRef.current || refreshInProgressRef.current) return;

      const { accessExp, idExp } = tokenInfoRef.current;
      if (!accessExp || !idExp) {
        log(LOG_CTX, '📡 No token info yet, fetching...');
        await fetchTokenInfo(false);
        return;
      }

      const now = Date.now();
      const accessRemaining = accessExp - now;
      const idRemaining = idExp - now;

      // Use the EARLIEST expiring token
      const earliestExp = Math.min(accessExp, idExp);
      const timeUntilExpiry = earliestExp - now;
      const expiringToken = accessExp <= idExp ? 'access' : 'id';

      log(LOG_CTX, `🔍 Checking: access=${Math.round(accessRemaining / 1000)}s, id=${Math.round(idRemaining / 1000)}s`);

      if (timeUntilExpiry <= TOKEN_REFRESH_BUFFER) {
        log(LOG_CTX, `⏰ ${expiringToken} token needs refresh (${Math.round(timeUntilExpiry / 1000)}s remaining)`);
        await fetchTokenInfo(true);
      }
    };

    const interval = setInterval(checkAndRefresh, TOKEN_CHECK_INTERVAL);
    return () => clearInterval(interval);
  }, [isAuthenticated, isLoading, fetchTokenInfo]);

  const forceRefresh = useCallback(async () => {
    log(LOG_CTX, '👆 Manual refresh requested');
    return fetchTokenInfo(true);
  }, [fetchTokenInfo]);

  return {
    tokenInfo,
    refreshCount,
    lastRefresh,
    refreshLog,
    error,
    isRefreshing,
    forceRefresh,
    fetchTokenInfo,
  };
};
